// IIFE entry for the embeddable widget's loader (widget.js).
//
// Behavior:
//   - Auto-mount on DOM ready. If `document.body` already exists at the
//     time the script evaluates (async scripts loaded after parse), mount
//     right away; otherwise wait for DOMContentLoaded.
//   - If `window.AevoEchoConfig.autoMount === false`, skip the auto
//     mount so the host page can call `window.AevoEcho.mount()`
//     manually (useful for apps that only want the widget on specific
//     routes).
//   - `open` / `toggle` mount first if needed, so a CTA can open the widget
//     even when `autoMount` is false.

import { aliasLegacyGlobal, migrateLegacyStorage, readHostConfig } from './legacy'
import { getMounted, mount, type MountOptions } from './mount'

export interface WidgetGlobal {
  version: string
  mount: (options?: MountOptions) => void
  open: () => void
  close: () => void
  toggle: () => void
  isOpen: () => boolean
  /** Apply any subset of a WidgetConfig to the mounted widget. */
  update: (partialConfig: unknown) => void
  /** Remove the widget and everything it attached; `mount()` can run again. */
  destroy: () => void
}

declare global {
  interface Window {
    AevoEcho?: WidgetGlobal
  }
}

function safeMount(options?: MountOptions): ReturnType<typeof mount> {
  try {
    return mount(options)
  } catch (err) {
    console.error('[aevo-echo] mount failed', err)
    return null
  }
}

const api: WidgetGlobal = {
  version: __WIDGET_VERSION__,
  mount: (options) => {
    safeMount(options)
  },
  open: () => safeMount()?.open(),
  close: () => getMounted()?.close(),
  toggle: () => safeMount()?.toggle(),
  isOpen: () => getMounted()?.isOpen() ?? false,
  update: (partialConfig) => getMounted()?.update(partialConfig),
  destroy: () => getMounted()?.destroy(),
}

function boot(): void {
  if (typeof window === 'undefined') return
  migrateLegacyStorage()
  if (!window.AevoEcho) window.AevoEcho = api
  aliasLegacyGlobal()

  const cfg = readHostConfig()
  if (cfg?.autoMount === false) return

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => safeMount(), { once: true })
  } else {
    safeMount()
  }
}

boot()
